// The backend returns human-readable error messages in `ApiError.error`, which we
// surface directly. The one exception is `captcha_missing` — a frontend-injected
// sentinel (see AuthService) signalling an empty hCaptcha token, which triggers a
// silent captcha regeneration + retry rather than a user-facing error (JSAD-6151).
export const CAPTCHA_MISSING_ERROR = 'captcha_missing';

export const isCaptchaMissingError = (raw: string | undefined): boolean =>
    raw === CAPTCHA_MISSING_ERROR;

// Fallback shown only when there is no backend message
// (e.g. network failures or captcha-token generation failures).
export const DEVICE_VERIFICATION_FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

// Shown when a verify/resend/send is attempted with no connectivity. hCaptcha cannot
// load offline, so without this guard the captcha widget retries in a loop and the user
// sees repeated challenge failures instead of a clear cause. Copy matches the
// existing offline toast in OrderLocationScreen/Pickup.
export const DEVICE_VERIFICATION_OFFLINE_MESSAGE = 'No internet connection';

// Shown when an MFA send/verify fails with a 401 (`AuthService` maps that to the `expired`
// sentinel — the bearer is dead, so there is no backend copy worth showing). Matches the
// existing copy in PhoneNumberScreen / PhoneNumberModal / LoginScreen.
export const EXPIRED_SESSION_MESSAGE = 'Your session expired — please sign in again.';

// The 401 sentinel `AuthService` synthesizes for every `phone/*` call (see
// `mapAccVerificationHttpError`). Not backend copy: shown raw it read as a bare "expired" toast.
export const EXPIRED_SESSION_ERROR = 'expired';

// Surface the backend's account-verification message directly (matching device
// verification); fall back to the generic string when there is none, and never show the
// captcha sentinel or the raw `expired` sentinel. Shared by both phone collectors
// (PhoneNumberScreen/PhoneNumberModal).
export const accVerificationErrorMessage = (raw: string | undefined): string => {
    if (!raw || isCaptchaMissingError(raw)) {
        return DEVICE_VERIFICATION_FALLBACK_MESSAGE;
    }
    return raw === EXPIRED_SESSION_ERROR ? EXPIRED_SESSION_MESSAGE : raw;
};
